import { Box, Stack, Typography, Button } from '@mui/material'
import { SearchX, Home, ShieldAlert, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { IconBadge } from 'components/UI/IconBadge'
import { ROUTES } from 'routes'

interface NotFoundProps {
  /**
   * 'page' (default): a genuinely unmatched route — generic "page not found".
   * 'scan': reached from a tag scan / verify link that failed to resolve to a
   * certificate — different copy, since "page not found" reads as a broken URL
   * when the actual problem is "we don't recognize this tag".
   */
  variant?: 'page' | 'scan'
  /**
   * Only meaningful for variant='scan'. false (default) = the certificate
   * genuinely doesn't exist (a bad/unregistered tag). true = the lookup itself
   * failed (network/server error) — surfaces a retry + a "contact the brand"
   * note instead of implying the tag is invalid.
   */
  isError?: boolean
}

/**
 * Customer-app 404 / scan-failure page. The app has no sidebar (public,
 * brand-subdomain viewer), so this is a clean, brand-theme-aware full-screen
 * page. Doubles as the "couldn't verify this item" state for a failed scan
 * (VRG-241-adjacent) — see `variant`.
 */
export const NotFound = ({ variant = 'page', isError = false }: NotFoundProps) => {
  const navigate = useNavigate()

  const isScan = variant === 'scan'

  const title = isScan ? "We couldn't verify this item" : 'Page not found'
  const description = isScan
    ? isError
      ? "Something went wrong while checking this tag. Please try again — if it keeps happening, contact the brand you purchased from for help."
      : "This tag couldn't be matched to a certificate. It may not be registered yet, or the link may be incorrect."
    : "We couldn't find the page you're looking for. It may have been moved, renamed, or never existed."

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ maxWidth: 420 }}>
        {/*
          The shared badge (§36). This was the THIRD variant of the same
          object: 72px with an `alpha(accent, .1)` disc, where the two error
          boundaries used 56px red and the two success screens 88px grey. The
          accent tint also meant a brand with a pale primary got a badge that
          all but vanished, since both the disc AND the glyph were derived from
          the same colour.

          A shield for a scan that did not resolve, a struck-through
          magnifier for a route that does not exist — the two states this page
          serves are genuinely different and should not share a glyph. Only the
          scan is toned as an alert: an unmatched URL is a nothing-happened,
          not a failure.
        */}
        <IconBadge
          icon={isScan ? ShieldAlert : SearchX}
          tone={isScan ? 'alert' : 'default'}
        />

        <Stack spacing={1} alignItems="center">
          <Typography variant="h1" sx={{ fontSize: '24px', fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '16px', color: 'text.secondary', lineHeight: 1.5 }}>
            {description}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2}>
          {isScan && isError && (
            <Button
              variant="outlined"
              disableElevation
              startIcon={<RefreshCw size={16} strokeWidth={1.75} />}
              onClick={() => window.location.reload()}
              sx={{ textTransform: 'none' }}
            >
              Try again
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            startIcon={<Home size={18} strokeWidth={1.75} />}
            onClick={() => navigate(ROUTES.HOME)}
            sx={{ textTransform: 'none' }}
          >
            Go to home
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}
